import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ErrorCode } from '@app-types/enums/error-code';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MailAccountContract } from '@app-types/api/mail-account';
import { MailAccountClient } from '@app-services/api/clients/mail-account.client';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { LoadingButtonComponent } from '@app-components/common/loading-button/loading-button.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { DrawerService } from '@app-services/drawer.service';
import { MatOption } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { ProviderContract } from '@app-types/api/provider';
import { ProviderClient } from '@app-services/api/clients/provider.client';
import { SkeletonFormDrawerComponent } from '@app-components/common/skeletons/skeleton-form-drawer/skeleton-form-drawer.component';
import { SomethingWentWrongComponent } from '@app-components/common/error/something-went-wrong/something-went-wrong.component';

@Component({
  selector: 'app-password-dialog',
  templateUrl: './password-dialog.component.html',
  styleUrls: ['./password-dialog.component.scss'],
  imports: [
    LoadingButtonComponent,
    MatFormFieldModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    MatOption,
    MatSelect,
    SkeletonFormDrawerComponent,
    SomethingWentWrongComponent,
  ],
})
export class MailAccountChangePasswordDialogComponent implements OnInit {
  @Input() public mailAccount: MailAccountContract;
  @Output() public submitEventEmitter = new EventEmitter<MailAccountContract>();
  public isLoading = false;
  public errorCode: ErrorCode | null = null;
  public form = new UntypedFormGroup({
    name: new UntypedFormControl(''),
    providerId: new UntypedFormControl(''),
    email: new UntypedFormControl(''),
    newPassword: new UntypedFormControl(null, [
      Validators.required,
      Validators.maxLength(256),
    ]),
    username: new UntypedFormControl('', [
      Validators.required,
      Validators.maxLength(256),
    ]),
  });

  public providers: ProviderContract[] = [];
  public providersIsLoading = false;
  public cannotLoadProviders = false;

  constructor(
    private mailAccountClient: MailAccountClient,
    public matchError: MatchError,
    public drawerService: DrawerService,
    private providerClient: ProviderClient
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadProviders();
    this.form = new UntypedFormGroup({
      name: new UntypedFormControl(this.mailAccount.name),
      providerId: new UntypedFormControl(this.mailAccount.providerId),
      email: new UntypedFormControl(this.mailAccount.email),
      newPassword: new UntypedFormControl(null, [
        Validators.required,
        Validators.maxLength(256),
      ]),
      username: new UntypedFormControl(this.mailAccount?.username ?? '', [
        Validators.required,
        Validators.maxLength(256),
      ]),
    });
  }

  async loadProviders(): Promise<void> {
    const loaderTimeout = setTimeout(() => {
      this.providersIsLoading = true;
    }, 500);

    this.cannotLoadProviders = false;
    try {
      const response = await this.providerClient.getAll();
      this.providers = response.providerContracts;
    } catch (e) {
      this.cannotLoadProviders = true;
      this.matchError.logError(e);
    } finally {
      clearTimeout(loaderTimeout);
      this.providersIsLoading = false;
    }
  }

  public hasError = (controlName: string, errorName: string) => {
    return this.form?.controls?.[controlName]?.hasError(errorName);
  };

  public async onSubmit(): Promise<void> {
    if (!this.form.valid) return;
    this.isLoading = true;
    this.drawerService.disabledDrawer(true);
    this.errorCode = null;
    try {
      const values = this.form.value;
      await this.mailAccountClient.changePassword({
        mailAccountId: this.mailAccount.mailAccountId,
        newPassword: values.newPassword,
        username: values.username,
        providerId: values.providerId,
      });
      this.drawerService.disabledDrawer(false);
      this.submitEventEmitter.emit();
      this.drawerService.closeDrawer();
    } catch (e) {
      this.drawerService.disabledDrawer(false);
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isLoading = false;
    }
  }
}
